import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { FormattedMessage, useIntl } from "react-intl";

export interface InvoiceListProps {
  invoices: any[];
  loading: boolean;
  onInvoiceClick: (invoice: any) => void;
  onRefresh: () => void;
  organizationName?: string;
  hideInvoiceType?: boolean;
}

// Helper functions
const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null || isNaN(Number(value))) return "-";
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateValue?: any): string => {
  if (!dateValue) return "-";
  if (typeof dateValue === "string" && /[a-zA-Z]/.test(dateValue)) return dateValue;
  
  let timestamp = dateValue;
  if (typeof dateValue === "string") {
    const isoDate = new Date(dateValue);
    if (!isNaN(isoDate.getTime())) {
      const day = isoDate.getDate().toString().padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day}-${months[isoDate.getMonth()]}-${isoDate.getFullYear()}`;
    }
    timestamp = Number(dateValue);
  }
  if (typeof timestamp === "number" && !isNaN(timestamp)) {
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day}-${months[date.getMonth()]}-${date.getFullYear()}`;
    }
  }
  return String(dateValue);
};

export function InvoiceList({ invoices, loading, onInvoiceClick, onRefresh, organizationName, hideInvoiceType = false }: InvoiceListProps) {
  const intl = useIntl();

  const getInvoiceTypeName = (invoiceType: string) => {
    switch (invoiceType) {
      case "TOTAL_USERS": return intl.formatMessage({ id: "organization.invoiceType.totalUsers" });
      case "ACTIVE_USERS": return intl.formatMessage({ id: "organization.invoiceType.activeUsers" });
      case "CARDS_TO_ACTIVE": return intl.formatMessage({ id: "organization.invoiceType.cardsToActive" });
      default: return "Standard";
    }
  };

  const getStatusDetails = (status?: string) => {
    const s = status?.toLowerCase() || 'pending';
    switch (s) {
      case 'paid': return { bg: '#ecfdf5', color: '#047857', icon: '✓', i18n: 'organization.paidStatus' };
      case 'pending': return { bg: '#fffbeb', color: '#b45309', icon: '⧖', i18n: 'organization.pendingStatus' };
      case 'cancelled': return { bg: '#fef2f2', color: '#dc2626', icon: '✕', i18n: 'organization.cancelledStatus' };
      case 'unpaid':
      case 'overdue': return { bg: '#fef2f2', color: '#b91c1c', icon: '✕', i18n: 'organization.unpaidStatus' };
      default: return { bg: '#fffbeb', color: '#b45309', icon: '⧖', i18n: 'organization.pendingStatus' };
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-2">
        <div className="w-8 h-8 border-3 border-indigo-100 border-t-indigo-500 rounded-full animate-spin"></div>
        <div className="text-indigo-500 font-semibold text-sm">
          <FormattedMessage id="organization.loadingInvoices" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto p-2">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            <FormattedMessage id="organization.invoiceListTitle" />
          </h2>
          <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider">Billing History</p>
        </div>
        <Button
          className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-all px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5"
          onClick={onRefresh}
        >
          <span>↻</span>
          <FormattedMessage id="organization.refreshInvoices" />
        </Button>
      </div>

      {/* Empty State */}
      {(!invoices || invoices.length === 0) ? (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-12 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-xl mb-2 shadow-sm border border-gray-100">📄</div>
          <h3 className="text-base font-bold text-gray-600 mb-1">
            <FormattedMessage id="organization.noInvoices" />
          </h3>
          <p className="text-gray-400 text-sm max-w-xs">
            {intl.formatMessage({ id: "organization.noInvoicesHint" })}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {invoices.map((invoice, index) => {
            const status = getStatusDetails(invoice.status || invoice.paymentStatus);
            return (
              <div
                key={invoice.invoiceNo || index}
                className="group relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-lg px-4 py-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-indigo-200"
                onClick={() => onInvoiceClick(invoice)}
              >
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-indigo-300 to-indigo-500 opacity-40 group-hover:opacity-80 transition-opacity"></div>
                
                <div className="flex flex-col lg:flex-row justify-between items-stretch gap-4 pl-3">
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-x-6 gap-y-2">
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide block mb-0.5">
                        <FormattedMessage id="organization.invoiceNumberLabel" />
                      </label>
                      <div className="text-sm font-bold text-gray-800">{invoice.invoiceNo || `#${index + 1}`}</div>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide block mb-0.5">
                        <FormattedMessage id="organization.paymentStatusLabel" />
                      </label>
                      <span 
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
                        style={{ backgroundColor: status.bg, color: status.color }}
                      >
                        <span className="text-xs font-extrabold">{status.icon}</span>
                        <FormattedMessage id={status.i18n} />
                      </span>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide block mb-0.5">
                        <FormattedMessage id="organization.invoiceForLabel" />
                      </label>
                      <div className="text-sm font-semibold text-gray-700 truncate max-w-[140px]">
                        {organizationName || "Subscription"}
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide block mb-0.5">Date</label>
                      <div className="text-sm font-medium text-gray-600">{formatDate(invoice.invoiceDate)}</div>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide block mb-0.5">Period</label>
                      <div className="text-xs font-medium text-gray-600">
                        {formatDate(invoice.fromDate)} → {formatDate(invoice.toDate)}
                      </div>
                    </div>

                    {!hideInvoiceType && (
                      <div>
                        <label className="text-[10px] font-semibold uppercase text-gray-400 tracking-wide block mb-0.5">Type</label>
                        <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold rounded uppercase">
                          {getInvoiceTypeName(invoice.invoiceType)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="lg:w-36 flex flex-row lg:flex-col justify-between items-center lg:items-end lg:justify-center">
                    <div className="text-right">
                      <div className="text-xl font-extrabold text-indigo-600 leading-tight">
                        ₹{formatCurrency(invoice.finalBillAmount ?? invoice.billAmount)}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                        {invoice.billedUsers ?? 0} <FormattedMessage id="organization.usersLabel" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                  {invoice.discountAmount > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs">✨</span>
                      <span className="text-[10px] font-bold text-green-700 uppercase">
                        Saved ₹{formatCurrency(invoice.discountAmount)}
                      </span>
                    </div>
                  ) : <div />}
                  <div className="flex items-center gap-1 text-indigo-500 opacity-60 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs font-semibold">
                      <FormattedMessage id="organization.clickToView" />
                    </span>
                    <span className="text-sm">→</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default InvoiceList;
